/**
 * UnsavedChangesDialog Component
 *
 * Confirmation dialog shown when user tries to leave a page with unsaved changes.
 * Offers options to save, discard, or cancel navigation.
 */

'use client';

import { Modal } from '../modal/Modal';
import { PrimaryButton } from '../button/PrimaryButton';
import { SecondaryButton } from '../button/SecondaryButton';

export interface UnsavedChangesDialogProps {
  isOpen: boolean;
  onSave: () => void;
  onDiscard: () => void;
  onCancel: () => void;
  isSaving?: boolean;
}

export function UnsavedChangesDialog({
  isOpen,
  onSave,
  onDiscard,
  onCancel,
  isSaving = false,
}: UnsavedChangesDialogProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      size="sm"
      closeOnOverlayClick={false}
      closeOnEsc={!isSaving}
      showCloseButton={false}
    >
      <div className="flex flex-col items-center text-center gap-4">
        {/* Warning Icon */}
        <div className="w-12 h-12 rounded-full bg-feedback-warning/10 flex items-center justify-center">
          <svg
            className="w-6 h-6 text-feedback-warning"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        {/* Title */}
        <h3 className="text-lg font-semibold text-text-primary">Unsaved Changes</h3>

        {/* Description */}
        <p className="text-text-secondary text-sm">
          You have unsaved changes. Would you like to save them before leaving?
        </p>

        {/* Actions */}
        <div className="flex flex-col w-full gap-2 mt-2">
          <PrimaryButton onClick={onSave} disabled={isSaving} isLoading={isSaving} fullWidth>
            Save Changes
          </PrimaryButton>
          <SecondaryButton onClick={onDiscard} disabled={isSaving} fullWidth>
            Discard Changes
          </SecondaryButton>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="w-full py-2 text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}
